import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './mochi-carousel.module.scss';
import { MochiCarouselIndicator } from './mochi-carousel-indicator';

import 'swiper/css';

import type { Swiper as SwiperType, SwiperOptions } from 'swiper';

export interface MochiCarouselProps {
  darkMode?: boolean;
  items: JSX.Element[];
  breakpoints?: SwiperOptions['breakpoints'];
  slidesPerView?: SwiperOptions['slidesPerView'];
  spaceBetween?: SwiperOptions['spaceBetween'];
  height?: SwiperOptions['height'];
  onSlideChange?: (index: number) => void;
  className?: string;
  swiperContainerClassName?: string;
}

export const MochiCarousel = ({
  darkMode = false,
  items,
  breakpoints,
  slidesPerView,
  spaceBetween,
  height,
  className,
  swiperContainerClassName,
  onSlideChange,
}: MochiCarouselProps) => {
  const startPosition = useRef<number | null>(null);

  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [itemMovingRatio, setItemMovingRatio] = useState<number>(0);

  const handleSlideChange = (swiper: SwiperType) => {
    const { activeIndex } = swiper;
    setCurrentIndex(activeIndex);
    onSlideChange?.(activeIndex);
  };

  const handleMoveTo = (index: number) => {
    swiper?.slideTo(index);
  };

  return (
    <div className={`${styles['container']} ${className}`}>
      <Swiper
        className={swiperContainerClassName}
        onSwiper={setSwiper}
        onSlideChange={handleSlideChange}
        centeredSlides
        height={height}
        draggable
        breakpoints={breakpoints}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        onSliderFirstMove={(swiper) => {
          startPosition.current = swiper.translate;
          setIsDragging(true);
        }}
        onSlideChangeTransitionStart={() => {
          startPosition.current = null;
          setIsDragging(false);
        }}
        onSetTranslate={(currentSwiper, currentPosition) => {
          const diff = currentPosition - (startPosition.current ?? 0);
          const firstSlideGridPosition = currentSwiper.snapGrid[0] ?? 0;
          const secondSlideGridPosition = currentSwiper.snapGrid[1] ?? 0;

          const singleItemWidth = Math.round(
            secondSlideGridPosition - firstSlideGridPosition,
          );

          if (isNaN(diff) || currentSwiper == null) return;

          // If diff is positive, it means the user is dragging to the right
          // If diff is negative, it means the user is dragging to the left
          const ratio = -(diff / singleItemWidth);

          setItemMovingRatio(ratio);
        }}
      >
        {items.map((item) => {
          return <SwiperSlide key={item?.key}>{item}</SwiperSlide>;
        })}
      </Swiper>
      <MochiCarouselIndicator
        darkMode={darkMode}
        total={items.length}
        index={currentIndex}
        isDragging={isDragging}
        ratio={itemMovingRatio}
        onMoveTo={handleMoveTo}
      />
    </div>
  );
};
